import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { ChevronDown, Lock } from 'lucide-react';
import { Btn, popIn } from './primitives';

/** Hero row that holds a primary CTA and the Actions dropdown trigger. */
export const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PopAnchor = styled.div`
  position: relative;
  flex: none;
`;

const ActionsMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  min-width: ${({ $minWidth }) => $minWidth}px;
  width: 300px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid color-mix(in oklch, ${({ theme }) => theme.border} 88%, #e6ece6);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(20, 40, 28, 0.14);
  z-index: 40;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fontBody};
  animation: ${popIn} 0.15s cubic-bezier(0.2, 0.7, 0.3, 1);
  max-height: min(70vh, calc(100vh - 24px));
  overflow-y: auto;

  ${({ $align }) => ($align === 'right'
    ? 'left: auto; right: 0;'
    : 'left: 0; right: auto;')}
`;

const MenuHeader = styled.div`
  padding: 14px 16px 12px;
  border-bottom: 1px solid color-mix(in oklch, ${({ theme }) => theme.border} 70%, #f0f3f0);
`;

const MenuEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSubtle || '#9aa69c'};
`;

const MenuTitle = styled.div`
  margin-top: 2px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
`;

const MenuSubtitle = styled.div`
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.textMuted};
`;

const MenuBody = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSubtle || '#9aa69c'};
  padding: 0 4px;
`;

const SectionBox = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 11px;
  padding: 4px;
`;

const ItemBtn = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  padding: 10px 11px;
  border: none;
  border-radius: 8px;
  background: ${({ $hover, $danger, $locked, theme }) => {
    if ($locked) return $hover ? theme.accentSoft : 'transparent';
    if (!$hover) return 'transparent';
    return $danger ? '#fbeceb' : theme.surface2;
  }};
  color: ${({ $danger, theme }) => ($danger ? '#c0392b' : theme.text)};
  font-family: inherit;
  text-align: left;
  cursor: ${({ $locked, disabled }) => ($locked ? 'pointer' : disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.12s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ItemIcon = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 1px;
  border-radius: 8px;
  background: ${({ $danger, theme }) => ($danger
    ? 'color-mix(in oklch, #c0392b 8%, ' + theme.surface + ')'
    : theme.surface)};
  border: 1px solid color-mix(in oklch, ${({ $danger, theme }) => ($danger ? '#c0392b 35%' : theme.border + ' 80%')}, #e6ece6);
  color: ${({ $danger, theme }) => ($danger ? '#c0392b' : theme.textMuted)};

  svg {
    flex: none;
  }
`;

const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ItemLabel = styled.span`
  font-size: 14px;
  font-weight: 650;
  color: ${({ $danger, theme }) => ($danger ? '#c0392b' : theme.text)};
  line-height: 1.35;
`;

const ItemDescription = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ $danger }) => ($danger ? 'color-mix(in oklch, #c0392b 72%, #6b7280)' : 'inherit')};
  ${({ $danger, theme }) => !$danger && `color: ${theme.textMuted};`}
`;

const ItemLock = styled.span`
  flex: none;
  margin-left: auto;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.textSubtle};
`;

export const ActionsDivider = styled.div`
  height: 1px;
  margin: 2px 4px;
  background: color-mix(in oklch, ${({ theme }) => theme.border} 70%, #f0f3f0);
`;

export const ActionsHint = styled.div`
  margin: 0 4px;
  padding: 10px 12px;
  border-radius: 10px;
  background: color-mix(in oklch, ${({ theme }) => theme.warning || 'oklch(0.74 0.15 72)'} 14%, ${({ theme }) => theme.surface});
  color: color-mix(in oklch, ${({ theme }) => theme.warning || 'oklch(0.74 0.15 72)'} 75%, ${({ theme }) => theme.text});
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
`;

/**
 * Page-level Actions dropdown — primary CTA plus a grouped popover menu.
 * Visual language matches GroupedRowActionSheet (group boxes, icons, header).
 */
export default function PageActionsMenu({
  primaryLabel,
  primaryIcon = null,
  onPrimaryClick,
  primaryDisabled = false,
  primaryLocked = false,
  menuTitle = 'Quick actions',
  menuSubtitle = null,
  menuMinWidth = 300,
  align = 'right',
  children,
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasMenu = Boolean(children);

  return (
    <HeroActions>
      {primaryLabel && (
        <Btn
          type="button"
          onClick={onPrimaryClick}
          disabled={primaryDisabled && !primaryLocked}
          $variant={primaryLocked ? 'ghost' : undefined}
        >
          {primaryLocked ? <Lock size={15} strokeWidth={2} aria-hidden="true" /> : primaryIcon}
          {primaryLabel}
        </Btn>
      )}
      {hasMenu && (
        <PopAnchor ref={anchorRef}>
          <Btn
            type="button"
            $variant="ghost"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((value) => !value)}
          >
            Actions
            <ChevronDown
              size={16}
              strokeWidth={2.25}
              aria-hidden="true"
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
            />
          </Btn>
          {open && (
            <ActionsMenuPanel
              $minWidth={menuMinWidth}
              $align={align}
              role="menu"
              aria-label={menuTitle}
            >
              <MenuHeader>
                <MenuEyebrow>Page actions</MenuEyebrow>
                <MenuTitle>{menuTitle}</MenuTitle>
                {menuSubtitle && <MenuSubtitle>{menuSubtitle}</MenuSubtitle>}
              </MenuHeader>
              <MenuBody>
                {typeof children === 'function' ? children(close) : children}
              </MenuBody>
            </ActionsMenuPanel>
          )}
        </PopAnchor>
      )}
    </HeroActions>
  );
}

/** Grouped section with label + tinted box — mirrors row action sheet groups. */
export function ActionsSection({ label, children }) {
  return (
    <SectionWrap>
      {label && <SectionLabel>{label}</SectionLabel>}
      <SectionBox>{children}</SectionBox>
    </SectionWrap>
  );
}

/** @deprecated Prefer ActionsSection — kept for backwards compatibility. */
export function ActionsSectionLabel({ children }) {
  return <SectionLabel>{children}</SectionLabel>;
}

/** Clickable menu row with optional icon and helper line. */
export function ActionsMenuItem({
  label,
  description = null,
  icon = null,
  disabled = false,
  locked = false,
  danger = false,
  onClick,
  onBlockedClick,
  closeActions,
}) {
  const [hover, setHover] = useState(false);
  const isHardDisabled = disabled && !locked;

  return (
    <ItemBtn
      type="button"
      role="menuitem"
      aria-disabled={locked || disabled || undefined}
      disabled={isHardDisabled}
      $hover={hover && !isHardDisabled}
      $danger={danger}
      $locked={locked}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        if (isHardDisabled) return;
        if (closeActions) closeActions();
        if (locked) {
          if (onBlockedClick) onBlockedClick();
          return;
        }
        if (onClick) onClick();
      }}
    >
      {icon && <ItemIcon $danger={danger} aria-hidden="true">{icon}</ItemIcon>}
      <ItemText>
        <ItemLabel $danger={danger}>{label}</ItemLabel>
        {description && <ItemDescription $danger={danger}>{description}</ItemDescription>}
      </ItemText>
      {locked ? (
        <ItemLock aria-hidden="true">
          <Lock size={14} strokeWidth={2} />
        </ItemLock>
      ) : null}
    </ItemBtn>
  );
}
